import React, { useState, useEffect } from 'react';
import { Eye, Mail, Plus, X, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { getBackendUrl } from '../utils/getBackendUrl';
import { toast } from 'sonner';

const API = getBackendUrl() + '/api';

export const PhotoViewAlertSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [recipientUserIds, setRecipientUserIds] = useState([]);
  const [customEmails, setCustomEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [teamUsers, setTeamUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        const [settingsRes, usersRes] = await Promise.all([
          fetch(`${API}/settings/photo-view-alerts`, { headers }),
          fetch(`${API}/users`, { headers }),
        ]);
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setEnabled(data.enabled || false);
          setRecipientUserIds(data.recipient_user_ids || []);
          setCustomEmails(data.custom_emails || []);
        }
        if (usersRes.ok) {
          const users = await usersRes.json();
          setTeamUsers((users || []).filter(u => u.role === 'admin'));
        }
      } catch (error) {
        console.error('Error loading photo view alert settings:', error);
        toast.error('Failed to load photo view alert settings');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const toggleUser = (userId) => {
    setRecipientUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const addEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (customEmails.some(e => e.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('This email is already in the list');
      return;
    }
    setCustomEmails(prev => [...prev, trimmed]);
    setNewEmail('');
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settings/photo-view-alerts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          enabled,
          recipient_user_ids: recipientUserIds,
          custom_emails: customEmails,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save settings');
      }
      toast.success('Photo view alert settings saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save photo view alert settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="glass-card p-6 flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-2 text-slate-300">Loading photo view alerts...</span>
      </div>
    );
  }

  return (
    <div className="glass-card p-6" data-testid="photo-view-alert-settings">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Eye className="w-6 h-6 text-orange-400" />
          <h3 className="text-xl font-bold text-white">Client Photo View Alerts</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-300">{enabled ? 'On' : 'Off'}</span>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="photo-view-alerts-toggle"
          />
        </div>
      </div>

      <p className="text-slate-300 text-sm mb-4">
        When a client views photos in a project gallery, one in-app notification alert (bell + push)
        is sent per visit (e.g. "Client X viewed 5 photos in Project Y") — visible to admins only.
        If nobody is selected, all admins are notified.
      </p>

      <div className={enabled ? 'space-y-5' : 'space-y-5 opacity-50 pointer-events-none'}>
        {/* Team member recipients */}
        <div>
          <Label className="text-slate-300 mb-2 block">
            Admins to Notify ({recipientUserIds.length} selected)
          </Label>
          {teamUsers.length === 0 ? (
            <p className="text-slate-400 text-sm">No team members found</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {teamUsers.map(u => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800/80"
                  data-testid={`photo-alert-user-${u.id}`}
                >
                  <input
                    type="checkbox"
                    checked={recipientUserIds.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <div className="min-w-0">
                    <span className="text-white block truncate">{u.name}</span>
                    <span className="text-slate-400 text-xs block truncate">{u.email} · {u.role}</span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Custom emails removed — alerts are now in-app notifications */}
      </div>

      <div className="flex justify-end pt-5">
        <Button
          onClick={saveSettings}
          disabled={isSaving}
          className="bg-green-600 hover:bg-green-700 min-w-[120px]"
          data-testid="photo-alert-save-btn"
        >
          {isSaving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save Settings</>
          )}
        </Button>
      </div>
    </div>
  );
};
